import type {DocumentDetailNavigationButtons} from './types';
import type {AnyObject} from 'typings/portaro.fe.types';
import type {FileUploadService} from '../../file/file-upload.service';
import type AvailabilityDataService from '../availability.data-service';
import type RecordService from '../record.service';
import type ExemplarService from '../exemplar.service';
import type ExemplarList from '../exemplar-list';
import type ReloaderService from 'shared/services/reloader.service';
import type FinishedResponseInteractionService from 'shared/services/finished-response-interaction.service';
import type LoanRequestService from '../../loan/loan-request.service';
import type CurrentAuthService from 'shared/services/current-auth.service';
import type LoanRequestDataService from '../../loan/loan-request.data-service';
import type WalkerService from 'shared/services/walker.service';
import type {RecordEditationDataService} from '../../record-editation/services/record-editation.data-service';
import type {DirectoryDataService} from 'shared/data-services/directory.data-service';
import type {SimilarDocumentsDataService} from '../similar-documents.data-service';
import type {FileService} from '../../file/file.service';
import type {StateParams} from '@uirouter/angularjs';
import {exists} from 'shared/utils/custom-utils';
import {getRecordPath} from 'shared/utils/data-service-utils';
import {DatasourceGroup, Kind, SearchType, Subkind} from 'shared/constants/portaro.constants';
import {FileCategoryId} from 'typings/portaro.be.types';
import type {
    Department,
    DirectoryInsight,
    DirectoryNode,
    Document,
    DocumentAvailability,
    Fond,
    Rec,
    RecordHolding,
    DirectoryNestedInsight,
    ViewableFile
} from 'typings/portaro.be.types';

export class KpDocumentDetailPageService {
    public static serviceName = 'kpDocumentDetailPageService';

    /*@ngInject*/
    constructor(private CONTEXT_PATH: string,
                private fileUploadService: FileUploadService,
                private availabilityDataService: AvailabilityDataService,
                private recordService: RecordService,
                private fileService: FileService,
                private exemplarService: ExemplarService,
                private currentDepartment: Department,
                private currentAuthService: CurrentAuthService,
                private loanRequestService: LoanRequestService,
                private finishedResponseInteractionService: FinishedResponseInteractionService,
                private directoryDataService: DirectoryDataService,
                private similarDocumentsDataService: SimilarDocumentsDataService,
                private loanRequestDataService: LoanRequestDataService,
                private recordEditationDataService: RecordEditationDataService,
                private walker: WalkerService,
                private reloader: ReloaderService,
                private $stateParams: StateParams) {
    }

    public async getAvailability(record: Document): Promise<DocumentAvailability> {
        return this.availabilityDataService.getByDocumentAndRequester(record, this.currentAuthService.getActiveUser().id);
    }

    public async getExemplarList(record: Document, exemplarsView: AnyObject): Promise<ExemplarList> {
        return this.exemplarService.getExemplarList(record, exemplarsView);
    }

    public createNavigationButtons(record: Document): DocumentDetailNavigationButtons {
        const urlParams = this.$stateParams;

        const current = record.id;
        const back = urlParams.back;
        const group = urlParams.group ? urlParams.group.split(',').filter((idString) => idString.length > 0) : null;

        function showAny(): boolean {
            return exists(back) || exists(group);
        }

        function showBack(): boolean {
            return exists(back);
        }

        function getBackLink(): string {
            return back;
        }

        function showPrevious(): boolean {
            return group && group.indexOf(current) > 0;
        }

        function showNext(): boolean {
            return group && group.indexOf(current) < group.length - 1;
        }

        function getPreviousPath(): string {
            if (!showPrevious()) {
                return '';
            }
            const currentId = group[group.indexOf(current) - 1];
            return getRecordPath(currentId) + getUrlParameters();
        }

        function getNextPath(): string {
            if (!showNext()) {
                return '';
            }

            const currentId = group[group.indexOf(current) + 1];
            return getRecordPath(currentId) + getUrlParameters();
        }

        function getUrlParameters(): string {
            let s = '';

            if (back) {
                s += '?';
                s += `back=${encodeURIComponent(back)}`;
            }

            if (group) {
                s += s.length > 0 ? '&' : '?';
                s += `group=${urlParams.group}`;
            }

            return s;
        }

        return {
            showAny,
            showBack,
            showPrevious,
            showNext,
            getBackLink,
            getPreviousPath,
            getNextPath
        }
    }

    public async getAllRecordHoldings(record: Document): Promise<RecordHolding[]> {
        return this.recordService.getAllRecordHoldings(record, this.currentDepartment);
    }

    public async getSimilarDocuments(record: Document): Promise<Document[]> {
        return this.similarDocumentsDataService.query(record.id);
    }

    public async uploadCoverImage(record: Document, files: FileList): Promise<void> {
        await this.fileUploadService.upload(`${this.CONTEXT_PATH}/api/records/covers/add`, files[0], {record: record.id});
        return this.reloader.reloadPage();
    }

    public async getDirectoryInsight(record: Rec): Promise<DirectoryInsight | null> {
        if (!record.directoryId) {
            return null;
        }

        return this.directoryDataService.getInsight(record.directoryId, null, [FileCategoryId.COVER]);
    }

    public async getNestedDirectoryInsight(record: Rec): Promise<DirectoryNestedInsight | null> {
        if (!record.directoryId) {
            return null;
        }

        return this.directoryDataService.getNestedInsight(record.directoryId, null, [FileCategoryId.COVER]);
    }

    public async getDirectoryTree(record: Rec): Promise<DirectoryNode | null> {
        if (!record.directoryId) {
            return null;
        }

        return this.directoryDataService.getDirectoryTree(record.directoryId);
    }

    public refreshPage(): void {
        this.reloader.reloadPage();
    }

    public async showSendByEmailForm(record: Document): Promise<boolean> {
        return this.recordService.sendTemplatedMail(record);
    }

    public async requestDocument(record: Document): Promise<void> {
        try {
            const actionResponse = await this.loanRequestService.requestDocument(record);
            await this.finishedResponseInteractionService.showSuccessResponseInToastIfPossible(actionResponse);
            // Page needs to be refreshed, otherwise loan modal would be displayed again
            // (https://support.kpsys.cz/issues/21876)
            this.reloader.reloadPage(1000);
        } catch (exceptionResponse) {
            await this.finishedResponseInteractionService.showFailedResponseInModalWindow(exceptionResponse);
        }
    }

    public async copyRecord(record: Document): Promise<void> {
        await this.recordService.copyRecordToSpecifiedFondAndAddRecordHoldingThrowingToModal(record);
    }

    public async updateWithExternalRecord(record: Document): Promise<void> {
        const initialFrontendParams: {
            isbnOrIssn?: string;
            author?: string;
            name?: string;
            publicationYear?: number;
        } = {};

        if (record.isbns && record.isbns.length > 0) {
            initialFrontendParams.isbnOrIssn = record.isbns[0].value;
        } else {
            if (record.primaryAuthors && record.primaryAuthors.length > 0) {
                initialFrontendParams.author = record.primaryAuthors[0].text;
            }
            if (record.publicationStartYear) {
                initialFrontendParams.publicationYear = record.publicationStartYear.value;
            }
            if (record.name) {
                initialFrontendParams.name = record.name;
            }
        }

        const modalModel = {
            staticSearchParams: {
                kind: [Kind.KIND_RECORD],
                subkind: Subkind.SUBKIND_DOCUMENT,
                type: SearchType.TYPE_SEARCH_SELECTION,
                datasourceGroup: DatasourceGroup.EXTERNAL
            },
            initialFrontendParams
        };

        await this.recordService.updateWithExternalRecordAndReloadThrowingToToast(record, modalModel);
    }

    public async deleteRecordHolding(recordHolding: RecordHolding, recordHoldings: RecordHolding[]): Promise<void> {
        await this.recordService.deleteRecordHolding(recordHolding, recordHoldings.length <= 1);
    }

    public async discardRecordHolding(recordHolding: RecordHolding): Promise<void> {
        await this.recordService.discardRecordHolding(recordHolding);
    }

    public async assignRecordHolding(record: Document): Promise<void> {
        await this.recordService.createRecordHolding(record);
    }

    public get isAdministrator(): boolean {
        return this.currentAuthService.isLoggedIn() && this.currentAuthService.hasRole('ROLE_ADMIN');
    }

    public async uploadRecordFiles(parentDirectoryId: number, files: File[]): Promise<ViewableFile[]> {
        try {
            return await Promise.all(files.map((file) => this.fileService.uploadFile(parentDirectoryId, file)));
        } catch (exceptionResponse) {
            await this.finishedResponseInteractionService.showFailedResponseInModalWindow(exceptionResponse);
            return [];
        }
    }

    public async getDocumentDownloadUrl(document: Document): Promise<string | null> {
        const result = await this.loanRequestDataService.requestDocumentDownloadUrl([{document}]);
        if (!exists(result)) {
            return null;
        }

        return result.url;
    }

    public async createRecordEditation(record: Document, fond: Fond): Promise<void> {
        const recordEditation = await this.recordEditationDataService.createNewEditation({
            record,
            fond,
            subkind: Subkind.SUBKIND_DOCUMENT
        });

        await this.walker.newSpaPage(`/record-editations/${recordEditation.id}`);
    }
}
